import React from "react";
import { Heart, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/components/auth/AuthProvider";
import { useGuestSaves } from "@/hooks/use-guest-saves";
import { toast } from "sonner";
import { analytics } from "@/lib/analytics";

interface SaveButtonProps {
  nursingHome: {
    nh_slug: string;
    center_name: string;
    county_slug: string;
    county_name?: string;
  };
  isInLeads?: boolean;
  onTrackClick: (type: string) => void;
  className?: string;
}

export function SaveButton({ 
  nursingHome, 
  isInLeads = false, 
  onTrackClick,
  className = ""
}: SaveButtonProps) {
  const { user } = useAuth();
  const { 
    isNursingHomeSaved, 
    saveNursingHome, 
    removeSavedNursingHome,
    getSavedCount 
  } = useGuestSaves();

  const isSaved = isNursingHomeSaved(nursingHome.nh_slug) || isInLeads;

  const handleSaveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onTrackClick('save_nursing_home');
    
    if (isSaved) {
      removeSavedNursingHome(nursingHome.nh_slug);
      analytics.saveNursingHome(nursingHome.nh_slug, nursingHome.center_name, 'unsave', 'card');
      toast.success("Removed from saved list", {
        description: `${nursingHome.center_name} has been removed from your saved nursing homes.`
      });
    } else {
      saveNursingHome(nursingHome);
      analytics.saveNursingHome(nursingHome.nh_slug, nursingHome.center_name, 'save', 'card');
      
      if (!user) {
        toast.success("Saved for later!", {
          description: `${nursingHome.center_name} added to your saved list. Create an account to sync across devices.`,
          action: {
            label: "Create Account",
            onClick: () => window.location.href = "/auth/signup"
          }
        });
      } else {
        toast.success("Saved!", {
          description: `${nursingHome.center_name} added to your saved list.`,
          action: {
            label: "View Saved",
            onClick: () => window.location.href = "/favorites"
          }
        });
      }
    }
  };

  const tooltipText = () => {
    return isSaved ? "Remove from saved" : "Save for later";
  };

  const iconColor = isSaved ? "text-red-500" : "text-gray-400 hover:text-red-500";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleSaveClick}
            className={`absolute top-2 right-2 bg-white/80 backdrop-blur-sm border border-gray-200 hover:bg-white hover:scale-110 transition-all duration-200 ${className}`}
            aria-label={isSaved ? "Remove from saved" : "Save nursing home"}
          >
            {isSaved ? (
              <Heart className={`h-4 w-4 ${iconColor} fill-current`} />
            ) : (
              <Heart className={`h-4 w-4 ${iconColor}`} />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipText()}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}